import { useState } from 'react';
import { Link } from 'react-router';
import { Confirm, DataTable, ErrorState, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatNumber, formatXaf } from '../../lib/format.js';
import { formatPlot, isForSale, isMultiUnit, lettingOf, statusBadgeFor, unitsSummary } from '../../lib/letting.js';

const STATUS_OPTIONS = [
  ['available', 'Available'],
  ['pending', 'Pending, someone is in the process'],
  ['rented', 'Rented out'],
  ['draft', 'Draft, hidden from tenants']
];

// Something for sale is for sale, under offer or sold.
const SALE_STATUS_OPTIONS = [
  ['available', 'For sale'],
  ['pending', 'Under offer'],
  ['rented', 'Sold'],
  ['draft', 'Draft, hidden from buyers']
];

// A building is published or a draft; each apartment carries its own status.
const BUILDING_STATUS_OPTIONS = [
  ['available', 'Published'],
  ['draft', 'Draft, hidden from tenants']
];

// Views and saves for one listing, pulled on demand so the table stays quick.
const StatsModal = ({ property, onClose }) => {
  const { data, loading, error } = useFetch(`/properties/${property.id}/stats`, [property.id]);

  const peak = data ? Math.max(1, ...data.viewsByDay.map((row) => row.count)) : 1;

  return (
    <Modal title={`How "${property.title}" is doing`} onClose={onClose} wide>
      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={error} /> : null}

      {data ? (
        <>
          <div className="stat-row">
            <div className="stat-tile">
              <span className="stat-value">{formatNumber(data.views)}</span>
              <span className="stat-label">Total views</span>
            </div>
            <div className="stat-tile">
              <span className="stat-value">{formatNumber(data.saves)}</span>
              <span className="stat-label">Tenants who saved it</span>
            </div>
            <div className="stat-tile">
              <span className="stat-value">
                {Object.values(data.applicationsByStatus).reduce((sum, value) => sum + value, 0)}
              </span>
              <span className="stat-label">Applications</span>
            </div>
          </div>

          <h3 className="chart-title">Views, last 30 days</h3>
          {data.viewsByDay.length === 0 ? (
            <p className="muted">No views recorded yet.</p>
          ) : (
            <div className="bar-chart" role="img" aria-label="Daily views over the last 30 days">
              {data.viewsByDay.map((row) => (
                <div key={row.day} className="bar-col" title={`${row.day}: ${row.count} views`}>
                  <span className="bar" style={{ height: `${(row.count / peak) * 100}%` }} />
                </div>
              ))}
            </div>
          )}

          <h3 className="chart-title">Applications by stage</h3>
          <ul className="kv-list">
            {Object.entries(data.applicationsByStatus).length === 0 ? (
              <li>
                <span>No applications yet</span>
              </li>
            ) : (
              Object.entries(data.applicationsByStatus).map(([status, count]) => (
                <li key={status}>
                  <StatusBadge status={status} />
                  <span>{count}</span>
                </li>
              ))
            )}
          </ul>
        </>
      ) : null}
    </Modal>
  );
};

const LandlordProperties = () => {
  useTitle('My listings');
  const toast = useToast();
  const { data, loading, error, reload } = useFetch('/properties/mine');
  const [deleting, setDeleting] = useState(null);
  const [takingOff, setTakingOff] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const items = data?.items || [];
  const visible =
    filter === 'all'
      ? items
      : filter === 'needs_attention'
        ? items.filter((item) => item.moderationStatus !== 'approved' || item.newApplicationCount > 0)
        : items.filter((item) => item.status === filter);

  const changeStatus = async (property, status) => {
    try {
      await api.patch(`/properties/${property.id}/status`, { status });
      const options = isForSale(property) ? SALE_STATUS_OPTIONS : isMultiUnit(property) ? BUILDING_STATUS_OPTIONS : STATUS_OPTIONS;
      const label = options.find(([value]) => value === status)?.[1] || status;
      toast.success(`"${property.title}" is now marked: ${label.split(',')[0].toLowerCase()}.`);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  // Off the market and back on: the listing, its photos and its whole history
  // stay; only whether the public can see it and apply or offer changes.
  const takeOff = async () => {
    setBusy(true);
    try {
      await api.patch(`/properties/${takingOff.id}/status`, { status: 'draft' });
      toast.success(`"${takingOff.title}" is off the market. Its history is kept, and you can put it back on any time.`);
      setTakingOff(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const putBack = async (property) => {
    try {
      await api.patch(`/properties/${property.id}/status`, { status: 'available' });
      toast.success(
        property.everListed ? `"${property.title}" is back on the market.` : `"${property.title}" is published.`
      );
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/properties/${deleting.id}`);
      toast.success('Listing deleted.');
      setDeleting(null);
      reload();
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  }

  const columns = [
    {
      key: 'title',
      header: 'Listing',
      render: (property) => (
        <div className="cell-listing">
          <Link to={`/properties/${property.id}`} className="cell-thumb">
            {property.coverImage ? <img src={property.coverImage} alt="" /> : <span>No photo</span>}
          </Link>
          <div>
            <Link to={`/properties/${property.id}`} className="cell-title">
              {property.title}
            </Link>
            <span className="muted">
              {[property.neighbourhood, property.city].filter(Boolean).join(', ')} -{' '}
              {isForSale(property)
                ? `For sale${property.plotSqm ? `, ${formatPlot(property.plotSqm)} plot` : ''}`
                : isMultiUnit(property)
                  ? unitsSummary(property) || `${lettingOf(property).short}, no ${lettingOf(property).units} added yet`
                  : `${property.rooms} room${property.rooms === 1 ? '' : 's'}`}
            </span>
            {property.moderationStatus === 'rejected' && property.moderationNote ? (
              <span className="cell-warning">Rejected: {property.moderationNote}</span>
            ) : null}
          </div>
        </div>
      )
    },
    {
      key: 'price',
      header: 'Price',
      render: (property) =>
        isForSale(property)
          ? `${formatXaf(property.price)}${property.negotiable ? ' (negotiable)' : ''}`
          : isMultiUnit(property) && property.unitCounts?.fromRent
            ? `from ${formatXaf(property.unitCounts.fromRent)} / month`
            : `${formatXaf(property.price)} / month`
    },
    {
      key: 'state',
      header: 'State',
      render: (property) => (
        <div className="cell-stack">
          <StatusBadge {...statusBadgeFor(property)} />
          {property.moderationStatus !== 'approved' ? <StatusBadge status={property.moderationStatus} /> : null}
        </div>
      )
    },
    {
      key: 'interest',
      header: 'Interest',
      render: (property) => (
        <div className="cell-stack">
          <span>{formatNumber(property.viewCount)} views</span>
          <span>
            {property.applicationCount} application{property.applicationCount === 1 ? '' : 's'}
            {property.newApplicationCount > 0 ? <strong className="new-flag"> {property.newApplicationCount} new</strong> : null}
          </span>
          <span className="muted">{property.mediaCount} files</span>
        </div>
      )
    },
    { key: 'created', header: 'Added', render: (property) => formatDate(property.createdAt) },
    {
      key: 'actions',
      header: 'Actions',
      render: (property) => (
        <div className="cell-actions">
          <select
            value={property.status}
            onChange={(event) => changeStatus(property, event.target.value)}
            aria-label={`Status for ${property.title}`}
          >
            {(isForSale(property) ? SALE_STATUS_OPTIONS : isMultiUnit(property) ? BUILDING_STATUS_OPTIONS : STATUS_OPTIONS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          {property.status === 'draft' ? (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => putBack(property)}>
              {property.everListed ? 'Put back on the market' : 'Publish'}
            </button>
          ) : property.status !== 'rented' ? (
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setTakingOff(property)}>
              Take off the market
            </button>
          ) : null}
          <Link to={`/landlord/properties/${property.id}/edit`} className="btn btn-outline btn-sm">
            Edit
          </Link>
          {isMultiUnit(property) ? (
            <Link to={`/landlord/properties/${property.id}/units`} className="btn btn-ghost btn-sm">
              {lettingOf(property).units.charAt(0).toUpperCase() + lettingOf(property).units.slice(1)}
            </Link>
          ) : null}
          {isForSale(property) ? (
            <Link to="/landlord/offers" className="btn btn-ghost btn-sm">
              Offers
            </Link>
          ) : (
            <Link to={`/landlord/applications?propertyId=${property.id}`} className="btn btn-ghost btn-sm">
              Applicants
            </Link>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setStats(property)}>
            Stats
          </button>
          {/* A sale of a registered plot is part of the land's history, so it is
              taken off the market rather than deleted. */}
          {property.parcelId ? null : (
            <button type="button" className="link-btn danger-link" onClick={() => setDeleting(property)}>
              Delete
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>My listings</h1>
          <p className="muted">{items.length} in total. Keep the status honest and your phone stays quieter.</p>
        </div>
        <Link to="/landlord/properties/new" className="btn btn-primary">
          Add a listing
        </Link>
      </header>

      <div className="tab-row">
        {[
          ['all', 'All'],
          ['available', 'Available'],
          ['pending', 'Pending'],
          ['rented', 'Rented'],
          ['draft', 'Off the market and drafts'],
          ['needs_attention', 'Needs attention']
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`tab ${filter === value ? 'is-current' : ''}`}
            onClick={() => setFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={visible}
        empty={{
          title: filter === 'all' ? 'No listings yet' : 'Nothing in this group',
          message: filter === 'all' ? 'Add your first house to start taking applications.' : 'Try another tab.'
        }}
      />

      {deleting ? (
        <Confirm
          title="Delete this listing?"
          message={`"${deleting.title}" and its photos will be removed for good, with its history. To stop showing it for a while, take it off the market instead - you can put it back on any time and nothing is lost.`}
          confirmLabel="Delete it"
          busy={busy}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
        />
      ) : null}

      {takingOff ? (
        <Confirm
          title={`Take "${takingOff.title}" off the market?`}
          message={
            isForSale(takingOff)
              ? 'It leaves the marketplace and stops taking offers. Anybody with an offer waiting is told it was taken off the market. Its listing history and the land\'s history are kept, and you can put it back on any time.'
              : 'It leaves the marketplace and stops taking applications. Its photos, details and history are kept, and you can put it back on any time.'
          }
          confirmLabel="Take it off the market"
          tone="primary"
          busy={busy}
          onConfirm={takeOff}
          onClose={() => setTakingOff(null)}
        />
      ) : null}

      {stats ? <StatsModal property={stats} onClose={() => setStats(null)} /> : null}
    </div>
  );
};

export default LandlordProperties;
